import { createFileRoute } from "@tanstack/react-router";
import { useState, useMemo } from "react";
import { Clock, GraduationCap, Landmark, Users } from "lucide-react";
import { Link } from "@tanstack/react-router";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import StatusChip from "@/components/shared/StatusChip";
import EmptyState from "@/components/shared/EmptyState";
import { SearchInput } from "@/components/shared/SearchInput";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { usePartnerStudents } from "@/hooks/usePartnerData";
import { TableSkeleton } from "@/components/shared/Skeletons";
import { getFileUrl } from "@/lib/api-client";

export const Route = createFileRoute("/partner/students/")({
  component: PartnerStudents,
});

type AssignedStudent = {
  id: string | number;
  name: string;
  email?: string;
  university?: string;
  course?: string;
  year?: number;
  status: string;
  avatar?: string;
};

function PartnerStudents() {
  const [search, setSearch] = useState("");
  const { data: students = [], isLoading } = usePartnerStudents();

  const filteredStudents = useMemo(() => {
    if (!students || students.length === 0) return [];
    return (students as AssignedStudent[]).filter(
      (s: AssignedStudent) =>
        (s.name || "").toLowerCase().includes(search.toLowerCase()) ||
        (s.email || "").toLowerCase().includes(search.toLowerCase()) ||
        (s.university || "").toLowerCase().includes(search.toLowerCase()),
    );
  }, [students, search]);

  const activeStudents = (students as AssignedStudent[]).filter(
    (s: AssignedStudent) => s.status === "active",
  ).length;

  const pendingStudents = (students as AssignedStudent[]).filter(
    (s: AssignedStudent) => s.status === "pending",
  ).length;

  if (isLoading) {
    return <TableSkeleton rows={5} columns={4} />;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="Assigned students"
        subtitle="View and manage students assigned to your placement slots."
        breadcrumbs={[{ label: "Dashboard", path: "/partner/dashboard" }, { label: "Students" }]}
      />

      <div className="grid grid-cols-2 gap-3 md:gap-4 lg:grid-cols-4">
        <StatCard label="Total students" value={students?.length || 0} icon={<Users />} />
        <StatCard label="Active" value={activeStudents} icon={<GraduationCap />} />
        <StatCard label="Pending" value={pendingStudents} icon={<Clock />} />
        <StatCard
          label="Universities"
          value={
            new Set(
              (students as AssignedStudent[])
                .map((s: AssignedStudent) => s.university)
                .filter(Boolean),
            ).size
          }
          icon={<Landmark />}
        />
      </div>

      <SectionCard
        title="Student directory"
        actions={
          <SearchInput
            placeholder="Search by name, email, university..."
            value={search}
            onChange={setSearch}
            className="sm:w-72"
          />
        }
        noPadding
      >
        {filteredStudents.length > 0 ? (
          <Table className="min-w-[760px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Student</TableHead>
                <TableHead>University</TableHead>
                <TableHead>Course</TableHead>
                <TableHead>Year</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredStudents.map((student: AssignedStudent) => (
                <TableRow key={student.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <Avatar className="size-8 rounded-full">
                        <AvatarImage src={getFileUrl(student.avatar)} alt="" />
                        <AvatarFallback className="rounded-full text-xs">
                          {(student.name || "S").charAt(0)}
                        </AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <p className="font-medium text-foreground">{student.name || "Unknown"}</p>
                        <p className="text-caption text-muted-foreground">{student.email || "-"}</p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-body">{student.university || "-"}</TableCell>
                  <TableCell className="text-body">{student.course || "-"}</TableCell>
                  <TableCell className="text-body tabular-nums">
                    {student.year ? `Year ${student.year}` : "-"}
                  </TableCell>
                  <TableCell>
                    <StatusChip status={student.status} />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="outline" asChild>
                      <Link to={`/partner/students/${student.id}` as string}>View profile</Link>
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <EmptyState
            bare
            icon={<Users />}
            title={search ? "No students found" : "No students assigned yet"}
            description={
              search
                ? "Try adjusting your search terms"
                : "Students will appear here once assigned to your placement slots."
            }
            actionLabel={!search ? "Create placement slot" : undefined}
            onAction={!search ? () => (window.location.href = "/partner/slots/new") : undefined}
          />
        )}
      </SectionCard>
    </div>
  );
}
